/**
 * `P` — draw a polygon (§9.3).
 *
 * Click to drop points, `Enter` / double-click / clicking the first point
 * closes it, `Backspace` removes the last point, `Esc` cancels, `Shift`
 * locks each segment to 15°. The ring is auto-simplified and checked for
 * self-intersection by the store before it becomes a region.
 */
import { angleLock } from '@/geo/snap'

import { distPct, idle, none, withState, type ToolEffect, type ToolHandler, type ToolResult } from './types'

/** A ring needs three points; below that `Enter` is a no-op, not a broken shape. */
export const MIN_POLYGON_POINTS = 3

export const polygonTool: ToolHandler = {
  id: 'polygon',
  shortcut: 'p',
  icon: 'PenTool',
  label: 'Draw shape',
  hint: 'Click around the edge of the thing. Click the first dot again to finish.',
  cursor: 'crosshair',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state
    const drawing = st.t === 'polygon.drawing' ? st : null

    if (ev.t === 'pointerdown' && ev.button === 0) {
      const at = drawing && ev.shift ? angleLock(drawing.points[drawing.points.length - 1] ?? ev.at, ev.at) : ev.at
      if (!drawing) return withState('polygon', { t: 'polygon.drawing', points: [at], cursor: at })

      const first = drawing.points[0]!
      if (drawing.points.length >= MIN_POLYGON_POINTS && distPct(at, first) <= cfg.closeRadius) {
        return close(drawing.points, cfg.activeKind.region)
      }
      return withState('polygon', { t: 'polygon.drawing', points: [...drawing.points, at], cursor: at })
    }

    if (ev.t === 'pointermove') {
      if (!drawing) return none(machine)
      const last = drawing.points[drawing.points.length - 1] ?? ev.at
      return withState('polygon', { ...drawing, cursor: ev.shift ? angleLock(last, ev.at) : ev.at })
    }

    if (ev.t === 'dblclick') {
      if (!drawing) return none(machine)
      return close(drawing.points, cfg.activeKind.region)
    }

    if (ev.t === 'key') {
      if (!drawing) return null
      if (ev.key === 'Enter') return close(drawing.points, cfg.activeKind.region)
      if (ev.key === 'Backspace') {
        const points = drawing.points.slice(0, -1)
        return points.length ? withState('polygon', { ...drawing, points }) : idle('polygon')
      }
      if (ev.key === 'Escape') return idle('polygon')
      return null
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') return idle('polygon')

    return null
  },
}

function close(points: readonly [number, number][], kind?: string): ToolResult {
  if (points.length < MIN_POLYGON_POINTS) {
    return {
      machine: { tool: 'polygon', state: { t: 'polygon.drawing', points: [...points], cursor: points[points.length - 1] ?? [0, 0] } },
      effects: [{ k: 'toast', message: 'A shape needs at least three points.', tone: 'info' }],
    }
  }
  const effects: ToolEffect[] = [{ k: 'beginGesture' }, { k: 'createRegion', ring: points.map((p) => [p[0], p[1]]), kind }, { k: 'endGesture' }]
  return idle('polygon', effects)
}
